// Decision-lineage presentation logic (TASK-20260728-researcher-observability-ui): the pure half of the
// Decisions tab — exit-rule metadata (spec §6, frozen), decision-type metadata, the steering-feed merge,
// exit-rule drift counts (§10), the tape's time-scale math, and the inspector's causality-chain builder.
// Pure functions only (unit-tested); the canvas/DOM halves live in the components.

// ---- Exit rules (spec §6, priority order — FROZEN; ids are part of the lineage contract). Colors are the
//      validated categorical set (dataviz six-checks pass on the app surface #0a0b0d); identity is never
//      color-alone — every rendering pairs the color with the id+label text or a glyph.
export const EXIT_RULES = [
  { id: 0, key: 'nodata', label: 'No data', detail: 'exit score NaN — delisting or feed gap', color: '#3987e5' },
  { id: 1, key: 'regime', label: 'Regime collapse', detail: 'regime < 0.15', color: '#d95926' },
  { id: 2, key: 'stop', label: 'Trailing stop', detail: 'peak_cum > 0.02 and (peak_cum − cum_ret) > 0.15', color: '#199e70' },
  { id: 3, key: 'maxhold', label: 'Max hold', detail: 'hours_held ≥ 336 (14 days)', color: '#c98500' },
  { id: 4, key: 'factor', label: 'Factor decay', detail: 'un-masked exit score < 0.40 at rebalance', color: '#d55181' },
];

const EXIT_RULE_BY_ID = new Map(EXIT_RULES.map((r) => [r.id, r]));

export function exitRuleMeta(id) {
  if (id === null || id === undefined) return { id: null, key: 'none', label: '—', detail: '', color: '#8a8b8d' };
  return EXIT_RULE_BY_ID.get(Number(id))
    ?? { id: Number(id), key: `rule${id}`, label: `Rule ${id}`, detail: '', color: '#8a8b8d' };
}

// ---- Decision types. Glyphs are the primary identity channel on the tape (▲ entry / ▼ exit / ◆ framework
//      close / ▪ book snapshot); color is secondary.
const DECISION_META = {
  entry: { glyph: '▲', label: 'Entry', color: '#1aa260' },
  exit: { glyph: '▼', label: 'Exit', color: '#e0533a' },
  external_close: { glyph: '◆', label: 'Framework close', color: '#d8a000' },
  book_snapshot: { glyph: '▪', label: 'Book snapshot', color: '#8a8b8d' },
};

export function decisionMeta(type) {
  return DECISION_META[type] ?? { glyph: '?', label: String(type), color: '#8a8b8d' };
}

// ---- Steering feed: merge the audit rows (server /steering) with the adjustments feed (existing
//      /adjustments read) into one newest-first timeline. Each event is tagged with its source so the UI
//      can render provenance; ids stay source-scoped (the merge never collides them).
export function mergeSteeringFeed(auditRows, adjustments) {
  const audit = (auditRows ?? []).map((a) => ({
    source: 'audit',
    id: `audit-${a.id}`,
    time: a.createdAt,
    actor: a.actor ?? '',
    action: a.action ?? '',
    detail: a.outcome ?? '',
    clientOrderId: a.clientOrderId ?? null,
  }));
  // The /adjustments read returns raw AutoTradeOrgAdjustment entities — the real field names are
  // userId/kind/status/orgId/failureReason/createdAt (AI-review MAJOR 2: an invented requestedBy shape
  // silently blanked the actor on every adjustment).
  const adj = (adjustments ?? []).map((x) => ({
    source: 'adjustment',
    id: `adj-${x.id}`,
    time: x.createdAt ?? null,
    actor: x.userId ?? '',
    action: x.kind ?? 'Adjustment',
    detail: [x.status, x.orgId, x.failureReason].filter(Boolean).join(' · '),
    clientOrderId: null,
  }));
  return [...audit, ...adj]
    .filter((e) => e.time != null)
    .sort((a, b) => Date.parse(b.time) - Date.parse(a.time));
}

// ---- §10 drift signal: exits-by-rule counts (distribution shifts are an early drift warning).
export function exitRuleDistribution(decisions) {
  const byRule = new Map();
  let total = 0;
  for (const d of decisions ?? []) {
    if (d.decisionType !== 'exit') continue;
    total += 1;
    const id = d.exitRule ?? -1;
    byRule.set(id, (byRule.get(id) ?? 0) + 1);
  }
  return { total, byRule };
}

// ---- Tape time-scale math (pure; the canvas component consumes it).
export function timeDomain(streams) {
  let min = Infinity;
  let max = -Infinity;
  for (const stream of streams ?? []) {
    for (const e of stream ?? []) {
      const t = Date.parse(e.time ?? e.barTime ?? '');
      if (Number.isNaN(t)) continue;
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
}

export function xForTime(t, domain, plotLeft, plotWidth) {
  const span = domain.max - domain.min;
  if (span <= 0) return plotLeft + plotWidth / 2;
  const ratio = Math.max(0, Math.min(1, (t - domain.min) / span));
  return plotLeft + ratio * plotWidth;
}

// ---- Formatting helpers (tabular-honest: null renders as an em-dash, never a fake zero).
export function formatBps(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${Math.abs(n).toFixed(1)} bps`;
}

export function formatNum(v, digits = 4) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function latencyMs(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  return Number.isNaN(ms) ? null : ms;
}

// Push a field only when the snapshot actually carries the key — absence stays absent (trust signal).
function pushField(fields, snapshot, key, label, digits = 4) {
  if (snapshot[key] === undefined || snapshot[key] === null) return;
  fields.push({ label, value: formatNum(snapshot[key], digits) });
}

// ---- The inspector's causality chain: regime gate → score vs threshold → rule → sizing → execution.
//      Returns [{ title, fields: [{label, value}], note? }] in narrative order; sections with no data are
//      omitted entirely rather than rendered empty.
export function describeDecision(row) {
  const snapshot = row?.signalSnapshot ?? {};
  const sections = [];

  // 1 — Regime gate (the market-state input every decision reads).
  const regime = [];
  pushField(regime, snapshot, 'regime', 'Regime score', 2);
  if (snapshot.regime == null) pushField(regime, snapshot, 'score_regime', 'Regime score', 2); // alias, never both
  pushField(regime, snapshot, 'regime_trend', 'Trend component', 2);
  pushField(regime, snapshot, 'regime_breadth', 'Breadth component', 2);
  pushField(regime, snapshot, 'regime_volcomp', 'Vol-compression component', 2);
  pushField(regime, snapshot, 'regime_btcmom', 'BTC-momentum component', 2);
  if (regime.length > 0) sections.push({ title: 'Regime gate', fields: regime });

  // 2 — The signal: entry (masked) / exit (un-masked) scores + the fired rule for exits.
  const signal = [];
  pushField(signal, snapshot, 'entry_score', 'Entry score (masked)', 3);
  pushField(signal, snapshot, 'exit_score', 'Exit score (un-masked)', 3);
  if (row?.decisionType === 'exit' || row?.decisionType === 'external_close') {
    const rule = exitRuleMeta(row.exitRule);
    if (rule.id !== null) {
      signal.push({ label: 'Fired rule', value: `EXIT ${rule.id} — ${rule.label}`, ruleId: rule.id });
    }
    pushField(signal, snapshot, 'hours_held', 'Hours held', 1);
    pushField(signal, snapshot, 'cum_ret', 'Cumulative return', 4);
    pushField(signal, snapshot, 'peak_cum', 'Peak cumulative', 4);
  }
  if (signal.length > 0) {
    sections.push({ title: row?.decisionType === 'entry' ? 'Entry signal' : 'Exit signal', fields: signal });
  }

  // 3 — Sizing: how the weight moved.
  const sizing = [];
  pushField(sizing, snapshot, 'sizing_vol', 'Sizing vol', 4);
  pushField(sizing, snapshot, 'weight_pre', 'Weight (pre)', 4);
  pushField(sizing, snapshot, 'weight_post', 'Weight (post)', 4);
  if (sizing.length > 0) sections.push({ title: 'Sizing', fields: sizing });

  // 4 — Execution: the joined terminal outcome(s). Absent = the decision never placed an order — say so.
  const outcomes = row?.outcomes ?? [];
  if (outcomes.length > 0) {
    for (const o of outcomes) {
      const fields = [];
      if (o.side) fields.push({ label: 'Side', value: o.side });
      if (o.status) fields.push({ label: 'Terminal status', value: o.status });
      if (o.expectedPrice != null) fields.push({ label: 'Expected (ref close)', value: formatNum(o.expectedPrice) });
      if (o.fillPrice != null) fields.push({ label: 'Fill (VWAP)', value: formatNum(o.fillPrice) });
      if (o.fillQty != null) fields.push({ label: 'Filled qty', value: formatNum(o.fillQty, 8) });
      if (o.slippageBps != null) fields.push({ label: 'Slippage', value: formatBps(o.slippageBps) });
      if (o.fees != null) fields.push({ label: 'Fees', value: formatNum(o.fees) });
      const place = latencyMs(o.decidedAt, o.placedAt);
      const resolve = latencyMs(o.decidedAt, o.resolvedAt);
      if (place != null) fields.push({ label: 'Decided → placed', value: `${formatNum(place, 0)} ms` });
      if (resolve != null) fields.push({ label: 'Decided → resolved', value: `${formatNum(resolve, 0)} ms` });
      sections.push({
        title: outcomes.length > 1 ? `Execution (${o.clientOrderId})` : 'Execution',
        fields,
      });
    }
  } else if (row?.decisionType === 'entry' || row?.decisionType === 'exit') {
    sections.push({
      title: 'Not executed',
      fields: [],
      note: 'No order was placed for this decision (unfundable or rejected before placement) — the decision is recorded, the execution half is honestly absent.',
    });
  }

  return sections;
}
